import { Heart, Coffee, MapPin, Truck, ArrowRight, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function FestivalBlend() {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const isSoldOut = false;

  const scrollToDetails = () => {
    const detailsSection = document.getElementById('details-section');
    detailsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOrderClick = () => {
    if (isSoldOut) return;
    setShowOrderModal(true);
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
  };

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />

      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/80 via-[#132947]/90 to-[#132947]" />
        </div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-sky-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl" />

        <div className="relative min-h-screen flex items-center justify-center px-6 max-w-7xl mx-auto py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            {/* Left side - Content */}
            <div className="text-center lg:text-left space-y-6">
              <div className="relative">
                <div className="absolute -left-4 top-8 w-1 h-32 bg-gradient-to-b from-sky-600 to-transparent rounded-full hidden lg:block" />
                <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight leading-none">
                  Festival
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-sky-300 mt-2">Blend</span>
                </h1>
              </div>

              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-sky-600/50" />
                  <span className="text-sky-400/70 text-sm font-semibold uppercase tracking-wider">Marblehead Festival of Arts</span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-sky-600/50" />
                </div>

                <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                  Art. Community. 60 years of showing up.
                </p>

                <p className="text-lg md:text-xl text-white/70 font-light">
                  A smooth, balanced medium roast celebrating creativity, community, and the quiet power of people gathering together.
                </p>

                <div className="inline-flex items-center gap-2 bg-sky-600/20 border border-sky-500/30 text-sky-300 px-4 py-2 rounded-full text-sm font-semibold">
                  <Heart className="w-4 h-4" />
                  Proceeds from every bag go to the Marblehead Festival of Arts
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button
                  onClick={handleOrderClick}
                  disabled={isSoldOut}
                  className={`group relative px-12 py-6 rounded-xl font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                    isSoldOut
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-sky-600 to-sky-400 text-white hover:from-sky-700 hover:to-sky-500 shadow-2xl shadow-sky-600/40 transform hover:scale-105 hover:shadow-sky-600/60'
                  } overflow-hidden`}
                >
                  {!isSoldOut && <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />}
                  <Coffee className={`w-6 h-6 ${!isSoldOut && 'group-hover:rotate-12'} transition-transform`} />
                  {isSoldOut ? 'Sold Out' : 'Order Now'}
                </button>
                <button
                  onClick={scrollToDetails}
                  className="bg-white/5 text-white border-2 border-white/20 px-10 py-6 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-sky-600/50 transition-all duration-300 backdrop-blur-md"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Right side - Product image */}
            <div className="flex items-center justify-center lg:justify-end relative">
              <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 to-transparent rounded-3xl blur-3xl scale-110" />
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-sky-600/30 to-transparent rounded-3xl blur-xl" />
                <img
                  src="/ChatGPT_Image_May_20,_2026_at_10_36_49_AM.png"
                  alt="Festival Blend Coffee"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500 border-2 border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details-section" className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Festival Blend
          </h2>
          <div className="w-20 h-1 bg-sky-600 mx-auto rounded-full" />
        </div>

        <div className="bg-gradient-to-br from-sky-600/10 to-transparent border border-sky-600/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-center mb-10">
            <a
              href="https://www.marbleheadfestival.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 bg-white rounded-2xl px-8 py-4 shadow-xl hover:shadow-2xl hover:scale-105 transform transition-all duration-300 group"
            >
              <img
                src="/MFoA.logo.final.png"
                alt="Marblehead Festival of Arts"
                className="w-20 h-20 object-contain"
              />
              <div className="text-left">
                <p className="text-navy-900 font-bold text-lg leading-tight text-[#1a2e5a]">Marblehead Festival of Arts</p>
                <p className="text-[#4a7bbf] text-sm font-medium">July 1–5, 2026 &nbsp;·&nbsp; 60th Anniversary</p>
                <p className="text-[#1a2e5a]/60 text-xs mt-1 group-hover:text-[#4a7bbf] transition-colors">marbleheadfestival.org ↗</p>
              </div>
            </a>
          </div>

          <div className="text-white/80 text-lg leading-relaxed max-w-3xl mx-auto space-y-4">
            <p className="font-semibold text-white text-2xl text-center mb-6">
              Entirely volunteer run since the beginning,<br />
              the festival is proof that art doesn't just<br />
              decorate a community — it helps hold one together.
            </p>

            <p>
              Created in collaboration with the Marblehead Festival of Arts, this smooth, balanced medium roast celebrates 60 years of creativity, community, and the quiet power of people showing up for one another.
            </p>

            <p>
              We made this coffee in that spirit. Warm, approachable, and crafted for gathering, Festival Blend is meant for slow mornings, shared tables, and the kinds of conversations that make a place feel like home.
            </p>

            <p className="font-medium text-white/90">
              We believe art matters, especially now.
            </p>

            <p>
              It invites wonder, builds connection, and reminds us there is still beauty worth making and protecting. This blend is a small tribute to that idea.
            </p>

            <p className="text-xl font-bold text-sky-400 text-center mt-8">
              Proceeds from every bag go directly to the Marblehead Festival of Arts,<br />
              <span className="text-white/90">helping sustain a tradition built entirely by volunteers</span><br />
              <span className="text-white/70 font-normal text-base">and fueled by community.</span>
            </p>

            <p className="text-white/70 italic text-center">
              More than half of every purchase supports the arts directly.<br />
              Brew a cup, give back, and be part of something enduring.
            </p>
          </div>
        </div>
      </div>

      {/* Bean Composition Section */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Coffee Composition
        </h2>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
          <h3 className="text-2xl font-bold text-white mb-4">Tasting Profile</h3>
          <p className="text-white/80 leading-relaxed">
            Festival Blend delivers a smooth, balanced medium roast experience crafted for gathering. Warm and approachable, it shines at slow mornings and shared tables alike.
          </p>
          <div className="grid md:grid-cols-2 gap-4 pt-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Roast Level</h4>
              <p className="text-white/70">Smooth Medium</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Best Brewing Methods</h4>
              <p className="text-white/70">Drip, Pour Over, French Press</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Tasting Notes</h4>
              <p className="text-white/70">Warm, Balanced, Smooth, Community</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Size</h4>
              <p className="text-white/70">10 oz (283g)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Section */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <div className="bg-gradient-to-br from-sky-600/15 to-sky-900/10 border border-sky-500/30 rounded-2xl p-8 md:p-12 text-center">
          <div className="flex justify-center mb-8">
            <a
              href="https://www.marbleheadfestival.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-xl hover:shadow-2xl hover:scale-105 transform transition-all duration-300 group"
            >
              <img
                src="/MFoA.logo.final.png"
                alt="Marblehead Festival of Arts"
                className="w-12 h-12 object-contain"
              />
              <span className="text-[#1a2e5a] font-bold text-sm">Official Partner &nbsp;·&nbsp; marbleheadfestival.org ↗</span>
            </a>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your Purchase Makes an Impact
          </h2>
          <p className="text-white/70 text-xl mb-8 max-w-2xl mx-auto">
            Every bag of Festival Blend directly supports the Marblehead Festival of Arts — a 60-year tradition built entirely by volunteers and fueled by community.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="text-3xl font-bold text-sky-400 mb-2">Proceeds</div>
              <div className="text-white/70 text-sm">from every bag go to the Festival of Arts</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="text-3xl font-bold text-sky-400 mb-2">60</div>
              <div className="text-white/70 text-sm">years of community arts in Marblehead</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="text-3xl font-bold text-sky-400 mb-2">100%</div>
              <div className="text-white/70 text-sm">volunteer run since the beginning</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Availability */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Order Today
          </h2>
          <p className="text-white/70 text-xl">Fresh roasted and ready to ship</p>
        </div>

        <div className="bg-gradient-to-br from-sky-600/10 to-transparent border-2 border-sky-600/40 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">$40</span>
              <span className="text-white/70 text-lg">per 10oz bag</span>
            </div>
            <p className="text-sky-400 font-semibold text-lg">
              Proceeds support the Marblehead Festival of Arts
            </p>
            <p className="text-amber-400/90 text-sm mt-2">Deliveries and shipping start the week of June 8th</p>
          </div>

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-sky-500" />
                <span className="text-white font-medium">Marblehead Delivery</span>
              </div>
              <span className="text-sky-400 font-semibold text-lg">$40</span>
            </div>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-sky-500" />
                <span className="text-white font-medium">US Shipping (included)</span>
              </div>
              <span className="text-white/90 font-semibold text-lg">$47</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-sky-600 to-sky-400 text-white hover:from-sky-700 hover:to-sky-500 shadow-xl shadow-sky-600/30 hover:scale-105 transform'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Festival Blend'}
            </button>
            <p className="text-white/60 text-sm mt-4">
              {isSoldOut ? 'Check back soon!' : 'Free local delivery • Shipping included for non-Marblehead orders'}
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience Festival Blend?
          </h3>
          <p className="text-white/70 text-xl mb-8">
            Order your bag today and support 60 years of community arts
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-sky-600 to-sky-400 text-white hover:from-sky-700 hover:to-sky-500 shadow-xl shadow-sky-600/30'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Now'}
            </button>
            <Link
              to="/"
              className="bg-white/10 text-white border border-white/20 px-8 py-5 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
            >
              Explore Bond Coffee
            </Link>
          </div>
          <p className="text-white/50 text-sm">
            Questions? Contact us at hello@bondcoffee.com
          </p>
        </div>
      </div>

      <Footer />

      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#132947] border-2 border-sky-600/30 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10 bg-[#132947]/80 rounded-full p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-3xl font-bold text-white mb-2 text-center">Choose Your Option</h2>
            <p className="text-sky-400 text-center text-sm font-semibold mb-3">Proceeds from every bag support the Marblehead Festival of Arts</p>
            <p className="text-amber-400/90 text-center text-sm mb-8">Deliveries and shipping will start the week of June 8th</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Marblehead Delivery */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-sky-600/50 transition-all group">
                <div className="text-center mb-4">
                  <MapPin className="w-12 h-12 text-sky-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Marblehead Delivery</h3>
                  <p className="text-white/70 text-sm mb-4">Free local delivery</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-sky-400 mb-1">$40</div>
                  <div className="text-white/50 text-sm mb-1">per 10oz bag</div>
                  <div className="text-sky-400/70 text-xs">proceeds support Festival of Arts</div>
                </div>
                <a
                  href="https://buy.stripe.com/5kQeVe6TB4OP2JD1Fo3VC0k"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-sky-600 to-sky-400 text-white py-3 rounded-lg font-bold text-center hover:from-sky-700 hover:to-sky-500 transition-all"
                >
                  Order - Marblehead
                </a>
              </div>

              {/* Shipping */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-sky-600/50 transition-all group">
                <div className="text-center mb-4">
                  <Truck className="w-12 h-12 text-sky-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Ship Anywhere in the US</h3>
                  <p className="text-white/70 text-sm mb-4">Shipping included</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-sky-400 mb-1">$47</div>
                  <div className="text-white/50 text-sm mb-1">per 10oz bag + shipping</div>
                  <div className="text-sky-400/70 text-xs">proceeds support Festival of Arts</div>
                </div>
                <a
                  href="https://buy.stripe.com/6oUeVefq7eppesl4RA3VC0l"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-sky-600 to-sky-400 text-white py-3 rounded-lg font-bold text-center hover:from-sky-700 hover:to-sky-500 transition-all"
                >
                  Order - Shipped
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
